import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  List,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import MobileNavListItem from "./MobileNavListItem";
import { useSelector } from "react-redux";
import { darkTheme, lightTheme } from "../../theme/Theme";
import PropTypes from "prop-types";
import MobileApplist from "./MobileApplist";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchApp from "../../hooks/useFetchApp";

import PepiscoImage from "./PepiscoImage";

export default function MobileNav({ drawerOpen, handleDrawerClose }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const drawerRef = useRef(null);
  const childDrawerRef = useRef(null);
  const [childDrawerOpen, setChildDrawerOpen] = useState(false);

  const isDarkTheme = useSelector((state) => state.theme);
  const { navigations } = useSelector((state) => state.nav);
  useFetchApp();
  const { apps } = useSelector((store) => store.appadmin);
  const isMobile = useMediaQuery("(max-width:600px)");

  // Filter out empty page groups (INTERNAL_GROUP with no children)
  const filteredNavigations = navigations?.filter((nav) => {
    if (nav?.type === "INTERNAL_GROUP") {
      // Hide page groups that have no children or empty children array
      return nav?.children && nav.children.length > 0;
    }
    return true; // Keep all non-page-group items
  });
  const handleItemClick = (item, parent) => {
    if (!parent) {
      handleDrawerClose();
    }
    setSelectedItem(item);
  };

  const handleOpenChildDrawer = () => setChildDrawerOpen(true);
  const handleCloseChildDrawer = () => setChildDrawerOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        (!childDrawerRef.current ||
          !childDrawerRef.current.contains(event.target))
      ) {
        handleDrawerClose();
        setChildDrawerOpen(false);
      }
    };

    if (drawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [drawerOpen]);

  return (
    <>
      {/* Parent Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        disableScrollLock
        hideBackdrop
        ModalProps={{ keepMounted: true }}
        data-testid="mobile-nav"
        sx={{
          "& .MuiDrawer-paper": {
            width: "80%",
            maxWidth: 350,
            height: "100vh",
            position: "fixed",
            overflow: "auto",
            boxShadow: "none",
            backgroundColor: isDarkTheme
              ? darkTheme.backgroundColorLayer3
              : lightTheme.backgroundColorLayer3,
          },
        }}
      >
        <Box
          ref={drawerRef}
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            background: isDarkTheme
              ? darkTheme.backgroundColorLayer3
              : lightTheme.backgroundColorLayer3,
          }}
        >
          <main style={{ flex: 1 }}>
            <List>
              {filteredNavigations?.map((item) => (
                <MobileNavListItem
                  key={item.navigationId}
                  item={item}
                  selectedItem={selectedItem}
                  handleItemClick={handleItemClick}
                  handleDrawerClose={handleDrawerClose}
                />
              ))}
            </List>
          </main>

          {/* Clickable area to open child drawer */}
          {isMobile && (
            <Box
              sx={{
                paddingX: "16px",
                paddingY: "8px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                alignItems: "center",
              }}
              onClick={handleOpenChildDrawer}
            >
              <PepiscoImage />
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: "150%",
                  cursor: "pointer",
                }}
              >
                All Apps
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Child Drawer */}
      <Drawer
        anchor="left"
        open={childDrawerOpen}
        onClose={handleCloseChildDrawer}
        hideBackdrop
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "80%",
            maxWidth: 350,
            height: "100vh",
            position: "fixed",
            backgroundColor: isDarkTheme
              ? darkTheme.backgroundColorLayer2
              : lightTheme.backgroundColorLayer2,
            zIndex: 1302, // Ensure this is higher than parent drawer
          },
        }}
      >
        <Box
          ref={childDrawerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            padding: "16px",
          }}
        >
          <Button
            onClick={handleCloseChildDrawer}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              fontWeight: 700,
              display: "flex",
              flexDirection: "row",
              gap: "5px",
              padding: 0,
            }}
          >
            <ArrowBackIcon
              sx={{ width: "16px", height: "16px", fill: "#005CBC" }}
            />
            <Typography
              sx={{
                color: "var(--Tokens-Button-Borderless, #005CBC)",
                fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                fontSize: "var(--Font-Size-Body, 14px)",
                fontStyle: "normal",
                fontWeight: 700,
              }}
            >
              Back
            </Typography>
          </Button>
          <Box
            sx={{
              marginTop: 2,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Box className="heading-container" data-testid="all-apps">
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  lineHeight: "140%",
                }}
              >
                All Apps
              </Typography>
            </Box>

            {apps?.map((item) => (
              <MobileApplist
                title={item?.title}
                key={item?.id}
                link={item?.link}
                // id={item?.emblemFileId}
                url={item.cdnUrl}
              />
            ))}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

MobileNav.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  handleDrawerClose: PropTypes.func.isRequired,
};
